import { useTranslation } from "react-i18next";

import { DatePicker, Form, Input, Spin, TableColumnsType } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { CustomTable, CustomTableForm } from "../../../../components";
import localStorageKey from "../../../../enumerations/localStorageKey";
import { useGetOrderHistory } from "../../../../hooks/UserCenter/usercenter.hooks";
import styles from "./style.module.scss";

const OrderInquiry = ({ nowTab }: { nowTab: string }) => {
  const [tableData, setTableData] = useState<any>([]);
  const [searchParams, setSearchParams] = useState({
    start_time: "",
    end_time: "",
    order_number: "",
  });
  const [hasToken, setHasToken] = useState<boolean>(false);
  const [isLoadingToken, setIsLoadingToken] = useState<boolean>(false);
  const localToken = window.localStorage.getItem(localStorageKey.TOKEN);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isLoadingOrderHistory,
    isError: isTransactionError,
    error: errorTransactionHistory,
  } = useGetOrderHistory({
    params: {
      order_number: searchParams.order_number,
      start_time: searchParams.start_time,
      end_time: searchParams.end_time,
    },
    options: {
      enabled: !!hasToken && !!searchParams,
      getNextPageParam: (lastPage: any, pages: any) => {
        if (pages.length < lastPage.last_page) {
          return pages.length + 1;
        } else {
          return undefined;
        }
      },
    },
  });
  const columns: TableColumnsType<any> = [
    {
      title: t("orderInquiry.order.date"),
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
    },
    {
      title: t("orderInquiry.order.id"),
      dataIndex: "order_number",
      key: "order_number",
      align: "center",
    },
    {
      title: t("orderInquiry.order.paymentMethod"),
      dataIndex: "pay_method",
      key: "pay_method",
      align: "center",
      render(value, record, index) {
        return record.pay_method
          ? t(`orderInquiry.payMethod.${record.pay_method}`)
          : "-";
      },
    },
    {
      title: t("orderInquiry.order.totalAmount"),
      dataIndex: "sell_price",
      key: "sell_price",
      align: "center",
      render(value, record, index) {
        return record.pay_status === 3 || record.pay_status === 5
          ? record.sell_price - record.refund_amount
          : record.sell_price;
      },
    },
    {
      title: t("orderInquiry.order.paymentStatus"),
      dataIndex: "pay_status",
      key: "pay_status",
      align: "center",
      render: (value) => {
        return t(`orderInquiry.order.payStatus${value}`);
      },
    },
    {
      title: t("orderInquiry.order.productStatus"),
      dataIndex: "delivery_status",
      key: "delivery_status",
      align: "center",
      render: (value) => {
        return t(`orderInquiry.order.deliveryStatus${value}`);
      },
    },
    {
      title: t("orderInquiry.order.orderReceipt"),
      dataIndex: "print",
      key: "print",
      align: "center",
      render: (value, record) => {
        return record.sell_price > 0 &&
          record.pay_status !== 0 &&
          record.pay_status !== 2 ? (
          <span
            style={{
              color: "var(--primary500)",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() =>
              window.open(`/orderreceipt?id=${record.order_number}`)
            }
          >
            {t("orderInquiry.order.printout.download")}
          </span>
        ) : (
          ""
        );
      },
    },
  ];
  useEffect(() => {
    if (data) {
      const page_length = data.pages.length;
      if (page_length === 1) {
        setTableData([...data.pages[0].data]);
      } else if (page_length > 1) {
        const newData: any = [];
        data.pages.map((page) => {
          page.data?.map((dataItem: any) => newData.push(dataItem));
        });
        setTableData(newData);
      }
    }
  }, [data]);

  useEffect(() => {
    setIsLoadingToken(true);
    setTimeout(() => {
      if (localToken) {
        setHasToken(true);
        setIsLoadingToken(false);
      }
    }, 3000);
  }, [localToken]);

  useEffect(() => {
    if (nowTab === "userinfo") {
      setTableData([]);
    }
  }, [nowTab]);

  return (
    <div className={styles.orderInquiry}>
      <div className={styles.title}>{t("normal.orderinquiry")}</div>
      <div className={styles.content}>
        <CustomTableForm
          form={form}
          onFinish={(val) => {
            setSearchParams({
              order_number: val?.order_number ?? "",
              start_time: val?.order_date
                ? dayjs(val?.order_date[0]).format("YYYY-MM-DD 00:00:00")
                : "",
              end_time: val?.order_date
                ? dayjs(val?.order_date[1]).format("YYYY-MM-DD 23:59:59")
                : "",
            });
          }}
          isSearchLoading={isLoadingOrderHistory}
          formItemList={[
            <Form.Item label={t("orderInquiry.order.date")} name="order_date">
              <RangePicker
                onChange={(val) => {
                  if (!val)
                    setSearchParams((prevState: any) => {
                      return { ...prevState, start_time: "", end_time: "" };
                    });
                }}
                inputReadOnly
                // showTime
                // format="YYYY-MM-DD HH:mm:ss"
              />
            </Form.Item>,
            <Form.Item label={t("orderInquiry.order.id")} name="order_number">
              <Input placeholder={t("normal.please.input", { content: "" })} />
            </Form.Item>,
          ]}
        />
        {isLoadingToken ? (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <Spin />
          </div>
        ) : (
          <CustomTable
            tableProps={{
              columns: columns,
              dataSource: tableData,
              scroll: { x: 1200 },
              rowKey: "id",
            }}
            hasMoreProps={{
              showInifineScroll: tableData.length > 0,
              hasMore: hasNextPage,
              isLoading: isFetchingNextPage,
              loadMore: fetchNextPage,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default OrderInquiry;
